import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader, Lock, Paperclip, FileText, X } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Offener Chat wird alle 5s aktualisiert. Kein WebSocket: produktiv laufen 4
// gunicorn-Worker ohne gemeinsamen Broker, eine In-Memory-Registry wuerde
// dort nicht ueber alle Worker hinweg funktionieren.
const POLL_INTERVAL_MS = 5000;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const formatBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return 'Heute';
  if (same(d, yesterday)) return 'Gestern';
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * Anhaenge liegen hinter einem authentifizierten Endpunkt - ein einfaches
 * <img src> koennte den Authorization-Header nicht mitschicken. Daher wird die
 * Datei als Blob geladen und als Object-URL eingebunden.
 */
const Attachment = ({ message }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const isImage = (message.attachment_mime || '').startsWith('image/');

  useEffect(() => {
    let url = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/messages/${message.id}/attachment`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [message.id]);

  if (failed) {
    return <p className="text-xs italic opacity-70">Anhang konnte nicht geladen werden</p>;
  }

  if (isImage) {
    return blobUrl ? (
      <a href={blobUrl} target="_blank" rel="noopener noreferrer">
        <img
          src={blobUrl}
          alt={message.attachment_name || 'Anhang'}
          className="rounded-lg max-h-64 w-auto object-contain cursor-zoom-in"
        />
      </a>
    ) : (
      <div className="h-32 w-48 rounded-lg bg-black/10 animate-pulse" />
    );
  }

  return (
    <a
      href={blobUrl || undefined}
      download={message.attachment_name || 'anhang'}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/10 hover:bg-black/20 transition-colors"
    >
      <FileText className="w-4 h-4 flex-shrink-0" />
      <span className="text-xs truncate max-w-[180px]">{message.attachment_name || 'Datei'}</span>
      <span className="text-[10px] opacity-70 flex-shrink-0">{formatBytes(message.attachment_size)}</span>
    </a>
  );
};

const ChatThread = ({ requestId, onActivity }) => {
  const [messages, setMessages] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);

  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const cursor = useRef(null);          // created_at der neuesten Nachricht
  const stickToBottom = useRef(true);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const token = () => localStorage.getItem('token');

  const markRead = useCallback(async () => {
    try {
      await axios.post(`${API}/messages/${requestId}/read`, {}, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      onActivityRef.current?.();
    } catch (e) {
      // Nicht kritisch: der Zaehler korrigiert sich beim naechsten Durchlauf
    }
  }, [requestId]);

  const fetchMessages = useCallback(async (initial = false) => {
    if (inFlight.current) return;          // keine ueberlappenden Abfragen
    if (!initial && document.visibilityState !== 'visible') return;
    inFlight.current = true;
    try {
      const params = {};
      if (!initial && cursor.current) params.since = cursor.current;

      const res = await axios.get(`${API}/messages/${requestId}`, {
        params,
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!alive.current) return;

      const incoming = res.data.messages || [];
      setMeta(res.data);

      if (incoming.length) {
        cursor.current = incoming[incoming.length - 1].created_at;
        setMessages((prev) => {
          // Nach id zusammenfuehren, damit optimistisch gezeigte und
          // nachgeladene Nachrichten nicht doppelt erscheinen.
          const byId = new Map(prev.map((m) => [m.id, m]));
          incoming.forEach((m) => byId.set(m.id, m));
          return Array.from(byId.values()).sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
        });
        const fromCounterpart = incoming.some((m) => m.sender_role !== res.data.role);
        if (fromCounterpart) markRead();
      } else if (initial) {
        setMessages([]);
      }
    } catch (err) {
      if (!alive.current) return;
      if (initial) toast.error('Chat konnte nicht geladen werden');
    } finally {
      inFlight.current = false;
      if (alive.current && initial) setLoading(false);
    }
  }, [requestId, markRead]);

  useEffect(() => {
    alive.current = true;
    cursor.current = null;
    setMessages([]);
    setLoading(true);

    fetchMessages(true).then(() => {
      if (alive.current) markRead();
    });

    const timer = setInterval(() => fetchMessages(false), POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchMessages(false);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive.current = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [requestId, fetchMessages, markRead]);

  useEffect(() => {
    if (stickToBottom.current) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [messages]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if ((!text && !pendingFile) || sending) return;

    setSending(true);
    try {
      let res;
      if (pendingFile) {
        const form = new FormData();
        form.append('message', text);
        form.append('file', pendingFile);
        res = await axios.post(`${API}/messages/${requestId}/attachment`, form, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        setPendingFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        res = await axios.post(`${API}/messages`, { request_id: requestId, message: text }, {
          headers: { Authorization: `Bearer ${token()}` }
        });
      }
      setDraft('');
      stickToBottom.current = true;
      setMessages((prev) => {
        const byId = new Map(prev.map((m) => [m.id, m]));
        byId.set(res.data.id, res.data);
        return Array.from(byId.values()).sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
      });
      cursor.current = res.data.created_at;
      onActivityRef.current?.();
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) toast.error(err.response?.data?.detail || 'Diese Anfrage nimmt keine Nachrichten mehr an.');
      else if (status === 429) toast.error('Zu viele Nachrichten. Bitte warte einen Moment.');
      else if (status === 413 || status === 400) toast.error(err.response?.data?.detail || 'Datei konnte nicht gesendet werden.');
      else toast.error('Nachricht konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-16">
        <Loader className="w-6 h-6 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  const myRole = meta?.role;
  const canWrite = meta?.chat_enabled && !meta?.frozen;

  let lastDay = null;

  return (
    <div className="flex flex-col h-full min-h-0">
      {meta?.counterpart_name && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
          {meta.counterpart_picture ? (
            <img
              src={getImageUrl(meta.counterpart_picture)}
              alt={meta.counterpart_name}
              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#F7B693] flex items-center justify-center text-[#2B2142] font-bold text-sm flex-shrink-0">
              {meta.counterpart_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-semibold text-[#2B2142] text-sm truncate">{meta.counterpart_name}</p>
            <p className="text-xs text-gray-500">
              {myRole === 'customer' ? 'Dienstleister' : 'Kunde'}
            </p>
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-2 bg-[#F4F2F2]/40"
      >
        {messages.length === 0 && (
          <p className="text-center text-sm text-gray-500 py-8">
            Noch keine Nachrichten. Schreib die erste!
          </p>
        )}

        {messages.map((m) => {
          const mine = m.sender_role === myRole;
          const day = dayLabel(m.created_at);
          const showDay = day && day !== lastDay;
          lastDay = day;

          return (
            <React.Fragment key={m.id}>
              {showDay && (
                <div className="flex justify-center py-2">
                  <span className="px-3 py-1 rounded-full bg-white text-xs text-gray-500 shadow-sm">
                    {day}
                  </span>
                </div>
              )}
              <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[75%] px-4 py-2.5 rounded-2xl shadow-sm ${
                    mine
                      ? 'bg-[#2B2142] text-white rounded-br-sm'
                      : 'bg-white text-[#2B2142] rounded-bl-sm'
                  }`}
                >
                  {m.attachment_key && (
                    <div className="mb-1.5">
                      <Attachment message={m} />
                    </div>
                  )}
                  {m.message && (
                    <p className="text-sm whitespace-pre-wrap break-words">{m.message}</p>
                  )}
                  <p className={`text-[10px] mt-1 text-right ${mine ? 'text-white/60' : 'text-gray-400'}`}>
                    {formatTime(m.created_at)}
                  </p>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {canWrite ? (
        <form onSubmit={handleSend} className="border-t border-gray-200 bg-white p-3">
          {pendingFile && (
            <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-[#F4F2F2] rounded-lg text-sm">
              <Paperclip className="w-4 h-4 text-[#2B2142] flex-shrink-0" />
              <span className="truncate flex-1 text-[#2B2142]">{pendingFile.name}</span>
              <span className="text-xs text-gray-500">{formatBytes(pendingFile.size)}</span>
              <button
                type="button"
                onClick={() => {
                  setPendingFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="p-1 hover:bg-gray-200 rounded"
                aria-label="Anhang entfernen"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <div className="flex gap-2 items-end">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > MAX_ATTACHMENT_BYTES) {
                toast.error('Datei zu groß (max. 10 MB)');
                e.target.value = '';
                return;
              }
              setPendingFile(f);
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-3 border-2 border-gray-200 hover:border-[#2B2142] rounded-xl transition-colors text-[#2B2142]"
            aria-label="Datei anhängen"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            rows={1}
            maxLength={4000}
            placeholder="Nachricht schreiben…"
            className="flex-1 resize-none max-h-32 px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none text-sm"
          />
          <button
            type="submit"
            disabled={sending || (!draft.trim() && !pendingFile)}
            className="p-3 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-xl transition-colors disabled:opacity-40"
            aria-label="Senden"
          >
            {sending ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
          </div>
        </form>
      ) : (
        <div className="border-t border-gray-200 bg-white p-4 flex items-center gap-2 text-sm text-gray-500">
          <Lock className="w-4 h-4 flex-shrink-0" />
          <span>
            {meta?.chat_enabled === false
              ? 'Diese Anfrage ist keinem Kundenkonto zugeordnet – ein Chat ist hier nicht möglich.'
              : 'Diese Anfrage ist abgeschlossen. Es können keine Nachrichten mehr gesendet werden.'}
          </span>
        </div>
      )}
    </div>
  );
};

export default ChatThread;
